/**
 * The signed-in layout: a top bar (product, backend, navigation, the
 * connectivity status, tenant and user, theme, sign-out) over the page. Presentational — the container
 * (`views/AppLayout.tsx`) owns the session, the router and the theme store.
 *
 * Navigation is plain links (`href`, so open-in-new-tab works) whose clicks
 * are handed to `onNavigate` for client-side routing.
 */
import { LogOutIcon, RadioTowerIcon, UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { isPlainClick } from '../lib/links.js';
import { cn } from '../lib/utils.js';
import type { Theme } from '../lib/theme.js';
import { ThemeToggle } from './ThemeToggle.js';
import { Badge } from './ui/badge.js';
import { Button } from './ui/button.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu.js';

export type NavItem = { readonly label: string; readonly href: string; readonly active: boolean };

export type ShellUser = { readonly userId: string; readonly email: string | null; readonly tenantId: string | null };

export type AppShellProps = {
  readonly backend: { readonly kind: 'cloud' | 'node'; readonly label: string };
  readonly user: ShellUser;
  readonly nav: readonly NavItem[];
  readonly onNavigate: (href: string) => void;
  readonly theme: Theme;
  readonly onToggleTheme: () => void;
  readonly onSignOut: () => void;
  /** The connectivity status (PLAN §10.2: always visible), rendered by the container. */
  readonly status?: ReactNode;
  /** The alert badge (phase 7b), rendered by the container; nothing while no alert holds. */
  readonly alerts?: ReactNode;
  /** The page fills the space under the top bar edge to edge and scrolls itself (the rundown editor). */
  readonly fullBleed?: boolean;
  /** Above the page, under the top bar: the vendor's announcements (phase 7b), rendered by the container. */
  readonly banner?: ReactNode;
  readonly children: ReactNode;
};

export function AppShell({ backend, user, nav, onNavigate, theme, onToggleTheme, onSignOut, status, alerts, fullBleed, banner, children }: AppShellProps) {
  return (
    <div className={cn('flex flex-col bg-background text-foreground', fullBleed === true ? 'h-screen' : 'min-h-screen')}>
      <header className="flex h-14 items-center gap-4 border-b bg-card px-4" data-testid="top-bar">
        <div className="flex items-center gap-2 font-semibold">
          <RadioTowerIcon className="size-5 text-onair" aria-hidden="true" />
          <span>Norna</span>
        </div>
        <Badge variant={backend.kind === 'cloud' ? 'standby' : 'preview'} data-testid="backend-badge" title={`Primary backend: ${backend.kind}`}>
          {backend.kind === 'cloud' ? 'Cloud' : 'Node'} · {backend.label}
        </Badge>
        <nav aria-label="Main" className="flex items-center gap-1">
          {nav.map((item) => (
            <a
              key={item.href}
              href={item.href}
              aria-current={item.active ? 'page' : undefined}
              onClick={(event) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                onNavigate(item.href);
              }}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                item.active && 'bg-accent text-accent-foreground',
              )}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {alerts ?? null}
          {status ?? null}
          {user.tenantId === null ? null : (
            <span className="hidden text-sm text-muted-foreground sm:inline" data-testid="tenant">
              {user.tenantId}
            </span>
          )}
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="user-menu">
                <UserIcon />
                <span className="max-w-40 truncate">{user.email ?? user.userId}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-medium">{user.email ?? user.userId}</div>
                <div className="text-xs text-muted-foreground">{user.tenantId ?? 'no tenant'}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onSignOut} data-testid="sign-out">
                <LogOutIcon />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      {banner ?? null}
      <main className={fullBleed === true ? 'min-h-0 flex-1' : 'flex-1 p-6'}>{children}</main>
    </div>
  );
}
